import React, { useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { User, Lock, Mail, Eye, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const { user, getAuthHeaders } = useAuth();
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords don't match");
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await axios.patch(
        `${API}/auth/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        },
        { headers: getAuthHeaders() }
      );

      toast.success("Password changed successfully!");
      
      // Clear form
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error("Error changing password:", error);
      const errorMessage = error.response?.data?.detail || "Failed to change password";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ml-64 p-8 min-h-screen bg-gradient-to-br from-[#0f172a] to-[#1e293b]">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <User className="w-10 h-10 text-[#f97316]" />
            Profile Settings
          </h1>
          <p className="text-[#94a3b8]">Manage your account settings and password</p>
        </div>

        {/* User Information Card */}
        <Card className="millmetrix-card p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-[#f97316]" />
            Account Information
          </h2>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-[#94a3b8]">
                Name
              </Label>
              <div className="text-white text-lg mt-1">{user?.name || 'N/A'}</div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-[#94a3b8]">
                <Mail className="w-4 h-4 inline mr-1" />
                Email
              </Label>
              <div className="text-white text-lg mt-1">{user?.email || 'N/A'}</div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-[#94a3b8]">
                Role
              </Label>
              <div className="text-white text-lg mt-1 capitalize">
                {user?.role === 'admin' ? (
                  <span className="text-[#f97316] font-bold">Administrator</span>
                ) : (
                  <span>User</span>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Change Password Card */}
        <Card className="millmetrix-card p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#f97316]" />
            Change Password
          </h2>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label htmlFor="current_password" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Current Password *
              </Label>
              <div className="relative">
                <Input
                  id="current_password"
                  type={showPasswords.current ? "text" : "password"}
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  className="millmetrix-input mt-2 pr-10"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#64748b] hover:text-white transition-colors"
                  tabIndex={-1}
                >
                  {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="new_password" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                New Password *
              </Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showPasswords.new ? "text" : "password"}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="millmetrix-input mt-2 pr-10"
                  required
                  minLength={6}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#64748b] hover:text-white transition-colors"
                  tabIndex={-1}
                >
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-[#64748b] mt-1">Minimum 6 characters</p>
            </div>

            <div>
              <Label htmlFor="confirm_password" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Confirm New Password *
              </Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  type={showPasswords.confirm ? "text" : "password"}
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  className="millmetrix-input mt-2 pr-10"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#64748b] hover:text-white transition-colors"
                  tabIndex={-1}
                >
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="bg-[#f97316] hover:bg-[#ea580c] text-white"
              disabled={loading}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
